import { PARTY_ROLE, PARTY_ROLE_DISPLAY, PARTY_STATUS, type PartyRoleId } from '@steerfi/shared';
import type React from 'react';
import { useState } from 'react';
import { createProvider, ProviderApiError } from '@/api/providers';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { flattenZodErrors, providerFormSchema } from '@/schemas/provider-form-schema';

interface AddProviderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const AddProviderModal: React.FC<AddProviderModalProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [provider_name, setProviderName] = useState('');
  const [provider_code, setProviderCode] = useState('');
  const [license_number, setLicenseNumber] = useState('');
  const [party_role_id, setPartyRoleId] = useState<number | ''>('');
  const [party_status_id, setPartyStatusId] = useState<number>(PARTY_STATUS.ACTIVE);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<string, string>>>({});
  const [rootError, setRootError] = useState<string | null>(null);

  const resetForm = () => {
    setProviderName('');
    setProviderCode('');
    setLicenseNumber('');
    setPartyRoleId('');
    setPartyStatusId(PARTY_STATUS.ACTIVE);
    setFieldErrors({});
    setRootError(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) resetForm();
    onOpenChange(next);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setRootError(null);
    const parsed = providerFormSchema.safeParse({
      provider_name,
      provider_code,
      license_number,
      party_role_id: party_role_id === '' ? undefined : party_role_id,
      party_status_id,
    });
    if (!parsed.success) {
      setFieldErrors(flattenZodErrors(parsed.error));
      return;
    }
    try {
      setSubmitting(true);
      await createProvider({
        provider_name: parsed.data.provider_name,
        provider_code: parsed.data.provider_code,
        license_number: parsed.data.license_number || null,
        party_role_id: parsed.data.party_role_id,
        party_status_id: parsed.data.party_status_id,
      });
      resetForm();
      onSuccess();
      onOpenChange(false);
    } catch (e: unknown) {
      if (e instanceof ProviderApiError && e.status === 409) {
        const msg =
          (typeof e.body?.message === 'string' && e.body.message) ||
          `Provider with code '${parsed.data.provider_code}' already exists`;
        setFieldErrors((prev) => ({ ...prev, provider_code: msg }));
      } else {
        setRootError(e instanceof Error ? e.message : 'Failed to create provider');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Provider</DialogTitle>
          <DialogDescription>Create a new payment-processing partner.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="add-provider-name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="add-provider-name"
              placeholder="e.g. Truist Financial"
              autoFocus
              value={provider_name}
              onChange={(ev) => setProviderName(ev.target.value)}
              aria-invalid={!!fieldErrors.provider_name}
            />
            {fieldErrors.provider_name && (
              <p className="text-sm text-destructive">{fieldErrors.provider_name}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="add-provider-code">
              Code <span className="text-destructive">*</span>
            </Label>
            <Input
              id="add-provider-code"
              placeholder="TRUIST"
              value={provider_code}
              onChange={(ev) => setProviderCode(ev.target.value)}
              aria-invalid={!!fieldErrors.provider_code}
            />
            {fieldErrors.provider_code && (
              <p className="text-sm text-destructive">{fieldErrors.provider_code}</p>
            )}
            <p className="text-xs text-muted-foreground">3–10 letters and numbers (e.g. TRUIST)</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="add-provider-license">License #</Label>
            <Input
              id="add-provider-license"
              placeholder="Optional"
              value={license_number}
              onChange={(ev) => setLicenseNumber(ev.target.value)}
              aria-invalid={!!fieldErrors.license_number}
            />
            {fieldErrors.license_number && (
              <p className="text-sm text-destructive">{fieldErrors.license_number}</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>
                Role <span className="text-destructive">*</span>
              </Label>
              <Select
                onValueChange={(v) => setPartyRoleId(Number(v))}
                value={party_role_id === '' ? '' : String(party_role_id)}
              >
                <SelectTrigger aria-invalid={!!fieldErrors.party_role_id}>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {(Object.entries(PARTY_ROLE) as [string, number][]).map(([, value]) => (
                    <SelectItem key={value} value={String(value)}>
                      {PARTY_ROLE_DISPLAY[value as PartyRoleId] ?? String(value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldErrors.party_role_id && (
                <p className="text-sm text-destructive">{fieldErrors.party_role_id}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                onValueChange={(v) => setPartyStatusId(Number(v))}
                value={String(party_status_id)}
              >
                <SelectTrigger aria-invalid={!!fieldErrors.party_status_id}>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(PARTY_STATUS.ACTIVE)}>Active</SelectItem>
                  <SelectItem value={String(PARTY_STATUS.INACTIVE)}>Inactive</SelectItem>
                </SelectContent>
              </Select>
              {fieldErrors.party_status_id && (
                <p className="text-sm text-destructive">{fieldErrors.party_status_id}</p>
              )}
            </div>
          </div>
          {rootError && <p className="text-sm text-destructive">{rootError}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
